import { observable, autorun, runInAction, when } from "mobx";
import get from "lodash/get";
import { tabState } from "./event-routing";

(function () {
  // escape hatch
  if (
    !document.querySelector('[data-tab-type="directory"]') ||
    !document.querySelector(".participant-dir")
  )
    return;

  // setup
  // * DOM shorthands
  const directoryTab = document.querySelector('[data-tab-type="directory"]'),
    editScreen = document.querySelector(".js-edit-profile"),
    indexScreen = directoryTab.querySelector(".directory-index"),
    dirWrapper = directoryTab.querySelector(".participant-dir"),
    searchInput = directoryTab.querySelector('input[type="search"]'),
    segmentSelect = directoryTab.querySelector(
      ".participant-dir__segment-filter select"
    );

  // * observable state
  let state = observable({
    mode: "index",
    page: 1,
    infiniteFinished: false,
    store: dirWrapper.innerHTML,
    userSearch: false,
  });

  // * ajax/fetch utils
  let controller;
  const dirEndpoint = `${window.location.origin}/wp-json/jumbo/v1/profile-links`;

  // events
  directoryTab.addEventListener("click", (e) => {
    // userlinks
    if (e.target.closest("[data-view-uid]")) {
      const {
        viewUid: uid,
        ccUid,
        name,
      } = e.target.closest("[data-view-uid]").dataset;
      state.user = { uid, ccUid, name };
      state.mode = "view";
    }

    // edit links
    if (e.target.closest("[data-edit-profile]")) {
      state.mode = "edit";
    }

    // back to dir links
    if (e.target.closest("[data-participant-dir]")) {
      state.mode = "index";
    }
  });
  // setup profile buttons in main content (not the sidebar)
  const profileButtons = Array.from(
    document.querySelectorAll(".button.type-profile")
  );
  profileButtons.forEach((button) => {
    button.addEventListener("click", function (e) {
      e.preventDefault();
      location.replace("#" + directoryTab.id);
      const { uid, ccUid, name } = button.dataset;
      runInAction(() => {
        state.user = { uid, ccUid, name };
        state.mode = "view";
      });
    });
  });
  // "infinite load" on-load OR first focus event
  // * if the viewport is so tall, or the query results so few,
  // * try to fill up the page with directory links
  when(() => tabState.activeTab === "#" + directoryTab.id, fillUpOnLoad);
  function fillUpOnLoad() {
    if (
      !state.infiniteFinished &&
      !state.userSearch &&
      dirWrapper.clientHeight === dirWrapper.scrollHeight
    ) {
      getProfiles(20, state.page * 20, WP.uid, null, null, true);
      state.page = state.page + 1;
    }
  }

  // "infinite load" scroll-bottom event
  dirWrapper.addEventListener("scroll", getNextPage);
  function getNextPage() {
    if (
      !state.userSearch &&
      !state.infiniteFinished &&
      !state.infiniteFetching
    ) {
      const { scrollTop, scrollHeight, offsetHeight } = this;
      const contentHeight = scrollHeight - offsetHeight;
      if (Math.ceil(scrollTop) >= contentHeight - 200) {
        state.infiniteFetching = true;
        getProfiles(20, state.page * 20);
        state.page = state.page + 1;
      }
    }
  }
  // search events
  if (searchInput) searchInput.addEventListener("input", performSearch);
  if (segmentSelect) segmentSelect.addEventListener("change", performSearch);
  function performSearch() {
    let search = get(searchInput, "value", null),
      segment = get(segmentSelect, "value", null);

    runInAction(() => {
      if (search || segment) {
        // render search results
        state.userSearch = true;
        getProfiles(-1, 0, WP.uid, search, segment);
      } else {
        // restore index from state
        state.userSearch = false;
        dirWrapper.innerHTML = state.store;
      }
    });
  }

  // run
  autorun(() => {
    const { mode, user } = state;

    if (mode === "index") {
      fadeIn(indexScreen);
    } else {
      fadeOut(indexScreen);
    }

    if (mode === "edit") {
      fadeIn(editScreen);
    } else {
      fadeOut(editScreen);
    }

    if (mode === "view") {
      const profile = document.querySelector(".view-profile");
      // * if another profile is already visible,
      // * fade it out before fading in the new one
      if (profile) {
        fadeOut(profile);
        setTimeout(() => {
          profile.parentNode.removeChild(profile);
          directoryTab.appendChild(Profile(user));
          fadeIn(document.querySelector(".view-profile"));
        }, 150);
      } else {
        directoryTab.appendChild(Profile(user));
        fadeIn(document.querySelector(".view-profile"));
      }
    } else {
      const profile = document.querySelector(".view-profile");
      if (profile) {
        fadeOut(profile);
        setTimeout(() => {
          profile.parentNode.removeChild(profile);
        }, 150);
      }
    }
  });

  // profile component
  function Profile(user) {
    const { uid, ccUid, name } = user;
    const wrapper = document.createElement("div");
    wrapper.classList.add("view-profile");
    wrapper.innerHTML = BackButton();
    wrapper.innerHTML += LoaderBar();

    const getProfile = (uid) => {
      const url = `${window.location.origin}/wp-json/jumbo/v1/profile/${uid}`;
      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          let profileMarkup = `
              ${BackButton()}
                <div class="profile-content">
                  ${
                    WP.directory.optInRequired &&
                    !data["wp-core"]["Directory Opt-in"]
                      ? `<div class="profile-header-note"><em>*Your profile is currently hidden from the event directory</em></div>`
                      : ""
                  }
                  <div class="profile-header">
                    ${
                      data["wp-core"].Avatar
                        ? `<div class="profile-header-avatar">${data["wp-core"].Avatar}</div>`
                        : ""
                    }
                    ${
                      data["wp-core"].Segment
                        ? `<div class="profile-header-segment tag tag--bright">${data["wp-core"].Segment}</div>`
                        : ""
                    }
                    ${
                      data["wp-core"].Name
                        ? `<div class="profile-header-name">${data["wp-core"].Name}</div>`
                        : ""
                    }
                  </div>`;

          profileMarkup += `${
            !!name && !!ccUid && WP.directory.hasAccompanyingChat
              ? `<div class="profile-message"><a data-dm-uid="${ccUid}" data-name="${name}" class="profile-message-link">
                  <span class="profile-message-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.9632 1.33691C7.08336 1.33691 2.29956 5.58691 2.29956 10.8109C2.31769 12.0667 2.59067 13.303 3.09929 14.4329C3.60792 15.5628 4.33986 16.5588 5.24365 17.3509L3.11092 21.9379C3.06772 22.0308 3.05242 22.1359 3.06715 22.2386C3.08187 22.3412 3.1259 22.4364 3.19311 22.5109C3.26031 22.5853 3.34736 22.6354 3.44214 22.654C3.53693 22.6727 3.63474 22.659 3.722 22.6149L9.4238 19.7529C10.5745 20.1093 11.766 20.2888 12.9632 20.2859C18.843 20.2859 23.6268 16.0359 23.6268 10.8109C23.6268 5.58591 18.843 1.33691 12.9632 1.33691Z" fill="currentColor"/>
                    </svg>
                  </span>
                  <span class="profile-message-label">Chat</span>
                  </a></div>`
              : ""
          }`;

          for (var key in data) {
            if (key !== "wp-core") {
              let value = data[key];
              if (Array.isArray(value)) {
                value = value.filter((value) => !!value).join(", ");
              }
              if (isValidUrl(value)) {
                value = `<a href="${value}" target="_blank">${value}</a>`;
              }

              profileMarkup += !!value
                ? `
                  <div class="profile-fields-field">
                    <span class="profile-fields-field-label">${key}</span>
                    <span class="profile-fields-field-value">${value}</span>
                  </div>
                `
                : "";
              patchProfileField({
                uid,
                field: { label: encodeURIComponent(key), value: data[key] },
              });
            }
          }

          profileMarkup += `</div>`;
          wrapper.innerHTML = profileMarkup;

          patchAvatars(uid);
        });
    };

    getProfile(uid);
    return wrapper;
  }

  // back btn (static markup)
  function BackButton() {
    return `
      <a class="button edit-profile__back-button" data-participant-dir>
        <span class="button-icon">
          ${WP.directory.backIcon}
        </span>
        Back to directory
      </a>
    `;
  }

  // loading bar (static markup)
  function LoaderBar() {
    return `
      <div class="MuiLinearProgress-root MuiLinearProgress-colorPrimary MuiLinearProgress-indeterminate" role="progressbar">
        <div class="MuiLinearProgress-bar MuiLinearProgress-barColorPrimary MuiLinearProgress-bar1Indeterminate"></div>
        <div class="MuiLinearProgress-bar MuiLinearProgress-bar2Indeterminate MuiLinearProgress-barColorPrimary"></div>
      </div>
    `;
  }

  // upload avatars
  document
    .getElementById("avatar-upload-form")
    .addEventListener("submit", (e) => {
      e.preventDefault();
    });

  const avatarFileField = document.getElementById("avatar-file");
  avatarFileField.addEventListener("change", () => {
    const loaderEl = document.getElementById("avatar-loader"),
      successEl = editScreen.querySelector(".avatar-success"),
      errorEl = editScreen.querySelector(".avatar-error");
    loaderEl.style.display = "block";
    successEl.textContent = "";
    errorEl.textContent = "";

    // check if a file is selected
    if (typeof avatarFileField.files[0] === "undefined") {
      loaderEl.style.display = "none";
      errorEl.textContent = "No file selected";
      return;
    }

    // check that mime type is acceptable...
    const file = avatarFileField.files[0],
      acceptable = ["image/jpeg", "image/png", "image/gif", "image/svg + xml"];
    if (!acceptable.includes(file.type)) {
      loaderEl.style.display = "none";
      errorEl.textContent =
        "Must be an image. Try a .jpeg, .png, .gif, or .svg file";
      return;
    }

    // format file as form data
    let formData = new FormData();
    formData.append("file", file);

    // upload file
    fetch(WP.directory.endpoint, {
      method: "POST",
      headers: {
        "X-WP-Nonce": WP.nonce,
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        if (!data.id) {
          // handle wordpress error
          console.error("wordpress error uploading media");
          errorEl.textContent = "Something went wrong, please try again.";
          successEl.textContent = "";
          loaderEl.style.display = "none";
          return;
        }

        // success: set user's simple_local_avatar to this new media
        const url = `${window.location.origin}/wp-json/wp/v2/users/me/`;
        fetch(url, {
          method: "PATCH",
          headers: {
            "X-WP-Nonce": WP.nonce,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            simple_local_avatar: { media_id: data.id },
          }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error("Network response was not ok");
            }
            return response.json();
          })
          .then((data) => {
            if (!data.id) {
              // handle wordpress error
              console.error("wordpress error uploading media");
              errorEl.textContent = "Something went wrong, please try again.";
              successEl.textContent = "";
              loaderEl.style.display = "none";
              return;
            }

            // success: show success message, patch dom avatars
            successEl.textContent = "Your new avatar is ready!";
            errorEl.textContent = "";
            loaderEl.style.display = "none";
            patchAvatars(data.id);
          })
          .catch((error) => {
            // handle transport error
            console.error("network/transport error setting avatar id", error);
            errorEl.textContent = "Something went wrong, please try again.";
            successEl.textContent = "";
            loaderEl.style.display = "none";
          });
      })
      .catch((error) => {
        // handle transport error
        console.error("network/transport error uploading avatar", error);
        errorEl.textContent = "Something went wrong, please try again.";
        successEl.textContent = "";
        loaderEl.style.display = "none";
      });
  });

  // ui helpers
  function fadeOut(el) {
    el.style.opacity = 0;
    setTimeout(() => (el.style.display = "none"), 150);
  }
  function fadeIn(el, flex = false) {
    el.style.display = flex ? "flex" : "block";
    el.style.opacity = 0;
    setTimeout(() => {
      el.style.opacity = 1;
    }, 1);
  }
  function patchAvatars(uid) {
    if (!uid) return;

    const url = `${window.location.origin}/wp-json/jumbo/v1/profile/${uid}`;
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if (data["wp-core"].Avatar) {
          const avatarsToPatch = Array.from(
            document.querySelectorAll(`[data-uid="${uid}"] .avatar`)
          );

          avatarsToPatch.forEach((oldAvatar) => {
            const template = document.createElement("template"),
              html = data["wp-core"].Avatar.trim();
            template.innerHTML = html;
            const newAvatar = template.content.firstChild;

            oldAvatar.parentNode.replaceChild(newAvatar, oldAvatar);
          });
        }
      });
  }
  function patchProfileField({ uid, field }) {
    if ((!uid, !field)) return;

    const { label, value } = field,
      elToPatch = document.querySelector(
        `.participant-dir__user-link[data-uid="${uid}"] [data-profile-label="${label}"]`
      );

    if (elToPatch) elToPatch.textContent = value;
  }
  function isValidUrl(string) {
    try {
      new URL(string);
    } catch (_) {
      return false;
    }
    return true;
  }
  function getProfiles(
    number = 20,
    offset = 0,
    exclude = parseInt(WP.uid),
    search = null,
    segment = null,
    onLoad = null
  ) {
    if (controller) controller.abort();
    controller = new AbortController();

    fetch(dirEndpoint, {
      signal: controller.signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ number, offset, exclude, search, segment }),
    })
      .then((response) => response.json())
      .then((data) => {
        state.infiniteFetching = false;

        // if this isn't a user search and it returns nothing, we have all the users
        if (!state.userSearch && !data.length) {
          dirWrapper.removeEventListener("scroll", getNextPage);
          state.infiniteFinished = true;
          return;
        }

        // if this is search mode, we need to re-render everything,
        // ? clear the container except for the "current user" div in preparation
        if (state.userSearch) {
          dirWrapper.innerHTML = dirWrapper.querySelector(
            ".participant-dir__current-user"
          ).outerHTML;
        }

        data.forEach((user) => {
          const { uid, name, avatar, segment, svg: arrowSvg } = user;
          const el = document.createElement("a");
          el.classList.add("participant-dir__user-link");
          el.dataset.uid = uid;
          el.dataset.viewUid = uid;
          el.dataset.ccUid = `${WP.directory.ccBase}_${uid}`;
          el.dataset.name = name;

          const template = `
            ${avatar}
            <span class='participant-dir__meta-col'>
              ${segment}
              <strong class='participant-dir__name'>${name}</strong>
            </span>
            ${arrowSvg}
          `;

          el.innerHTML = template;
          dirWrapper.appendChild(el);
        });

        // if this isn't a search, we maintain a store of all our users received,
        // ? so that we can restore our index when search params are cleared, without fetching
        if (!state.userSearch) {
          runInAction(() => {
            state.store = dirWrapper.innerHTML;
          });
        }

        // callback for "fill up on load functionality (very tall screens)"
        if (onLoad) {
          fillUpOnLoad();
        }
      })
      .catch((error) => {
        // handle errors
        // ? 'AbortError' is expected when aborting from typing in search
        if (error.name !== "AbortError") console.error(error);
      });
  }
})();
